import {
  DEFAULT_PAGE_SIZE,
  type ImportOrderResponse,
  type ImportOrderRow,
  type ImportStage,
} from '@palaccount/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/api';
import { throwApiError } from '@/lib/server-messages';

import type { RailStage } from './import-stages';

/**
 * PUR-06 data layer.
 *
 * Every mutation below returns the WHOLE shipment and every one of them seeds
 * the detail cache with it. That is not a convenience: §15 requires the
 * allocation to be recomputed from the charge rows on every read, so a screen
 * that patched a charge locally would be showing landed costs the server does
 * not agree with. The server's recomputed answer is the only answer.
 */

/**
 * ts-rest marks `body` optional on the generated argument, so reading the type
 * straight off it hands back `T | undefined` — and under
 * exactOptionalPropertyTypes handing `undefined` back to an optional property
 * is an error. Strip it once here rather than at every call site.
 */
type Body<T extends (args: never) => unknown> = Exclude<
  Parameters<T>[0] extends { body?: infer B } ? B : never,
  undefined
>;

export const IMPORTS_KEY = ['imports'] as const;

export function importKey(importId: string) {
  return [...IMPORTS_KEY, importId] as const;
}

/**
 * PUR-06 §4's list, one page at a time (NFR-42). Returns the whole body — the
 * rows AND the meta `<Pager>` draws from — so the range and the table cannot
 * disagree about what "Showing 1–8 of 12" means.
 */
export interface ImportListFilters {
  stage?: ImportStage | undefined;
  /** Wave 3B — the list's window on the shipment's order date, both inclusive. */
  dateFrom?: string | undefined;
  dateTo?: string | undefined;
}

/**
 * Wave 3B — whether the list draws its search box: `true` the moment the
 * contract takes `q` (the annotation flips and the typecheck demands it).
 */
export const IMPORTS_SEARCHABLE: 'q' extends keyof ImportListFilters ? true : false = false;

function importQuery(filters: ImportListFilters) {
  return {
    ...(filters.stage ? { stage: filters.stage } : {}),
    ...(filters.dateFrom ? { dateFrom: filters.dateFrom } : {}),
    ...(filters.dateTo ? { dateTo: filters.dateTo } : {}),
  };
}

export function useImportOrders(filters: ImportListFilters = {}, page = 1, pageSize = DEFAULT_PAGE_SIZE) {
  return useQuery({
    queryKey: [...IMPORTS_KEY, 'list', filters, page, pageSize],
    queryFn: async () => {
      const response = await api.imports.list({ query: { ...importQuery(filters), page, pageSize } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    // The figures update without the table blinking back to a skeleton.
    placeholderData: keepPreviousData,
  });
}

/** The export's page read — the same query the screen shows, imperative so `fetchAllPages` can walk it. */
export async function fetchImportOrdersPage(
  filters: ImportListFilters,
  page: number,
  pageSize: number,
): Promise<{ rows: ImportOrderRow[]; total: number }> {
  const response = await api.imports.list({ query: { ...importQuery(filters), page, pageSize } });
  if (response.status !== 200) throwApiError(response.status, response.body);
  return { rows: response.body.orders, total: response.body.total };
}

/** Wave 3B — PUR-06's list tiles over the whole register (PAGINATION.md §3 step 10). */
export function useImportStats() {
  return useQuery({
    queryKey: [...IMPORTS_KEY, 'stats'],
    queryFn: async () => {
      const response = await api.imports.stats();
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

export function useImportOrder(importId: string | undefined) {
  return useQuery({
    queryKey: importKey(importId ?? ''),
    enabled: Boolean(importId),
    queryFn: async (): Promise<ImportOrderResponse> => {
      const response = await api.imports.get({ params: { importId: importId! } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

/** Shared by every mutation: replace the detail cache, invalidate the lists. */
function useShipmentWriter<TVars>(
  call: (vars: TVars) => Promise<ImportOrderResponse>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: call,
    onSuccess: (order) => {
      queryClient.setQueryData(importKey(order.id), order);
      void queryClient.invalidateQueries({ queryKey: IMPORTS_KEY });
    },
  });
}

export function useCreateImportOrder() {
  return useShipmentWriter(async (body: Body<typeof api.imports.create>) => {
    const response = await api.imports.create({ body });
    if (response.status !== 201) throwApiError(response.status, response.body);
    return response.body;
  });
}

export function useUpdateImportOrder(importId: string) {
  return useShipmentWriter(async (body: Body<typeof api.imports.update>) => {
    const response = await api.imports.update({ params: { importId }, body });
    if (response.status !== 200) throwApiError(response.status, response.body);
    return response.body;
  });
}

export function useSetCharges(importId: string) {
  return useShipmentWriter(async (body: Body<typeof api.imports.setCharges>) => {
    const response = await api.imports.setCharges({ params: { importId }, body });
    if (response.status !== 200) throwApiError(response.status, response.body);
    return response.body;
  });
}

/**
 * §5e's "type one total" box. It is an INPUT, not a store — the server spreads
 * the number across the charge rows and hands back the same shipment shape
 * every other caller gets (BR-PUR-06-24).
 */
export function useSetLandedTotal(importId: string) {
  return useShipmentWriter(async (total: string) => {
    const response = await api.imports.setLandedTotal({ params: { importId }, body: { total } });
    if (response.status !== 200) throwApiError(response.status, response.body);
    return response.body;
  });
}

export function useAdvanceStage(importId: string) {
  return useShipmentWriter(async (vars: { stage: RailStage; reason?: string }) => {
    const response = await api.imports.advance({
      params: { importId },
      body: { stage: vars.stage, ...(vars.reason ? { reason: vars.reason } : {}) },
    });
    if (response.status !== 200) throwApiError(response.status, response.body);
    return response.body;
  });
}

/** Wave 5E — one line's HS code, origin, duty %, weight and volume. */
export function useUpdateImportLine(importId: string) {
  return useShipmentWriter(
    async (vars: { lineId: string; body: Body<typeof api.imports.updateLine> }) => {
      const response = await api.imports.updateLine({
        params: { importId, lineId: vars.lineId },
        body: vars.body,
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  );
}

/** Wave 5E — cancel the shipment, with GEN-59's typed reason. */
export function useCancelImport(importId: string) {
  return useShipmentWriter(async (reason: string) => {
    const response = await api.imports.cancel({ params: { importId }, body: { reason } });
    if (response.status !== 200) throwApiError(response.status, response.body);
    return response.body;
  });
}

export function useReceiveShipment(importId: string) {
  return useShipmentWriter(
    async (vars: { lines: { lineId: string; receivedQty: string }[]; receiptDate: string }) => {
      const response = await api.imports.receive({ params: { importId }, body: vars });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  );
}
